import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Clock, Radio } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { gameService } from '../../../services/gameService';
import { subscriptionService } from '../../../services/subscriptionService';
import { useFeatureAccess } from '../../../hooks/useFeatureAccess';
import UpgradeModal from '../../UpgradeModal';
import type { Game } from '../../../types';

interface TennisMatchStatusCardProps {
  game: Game;
  onStarted: () => void;
}

const STATE_LABEL: Record<string, string> = {
  scheduled: 'Scheduled',
  live: 'Live',
  complete: 'Complete',
  abandoned: 'Abandoned',
};

const STATE_BADGE_CLASS: Record<string, string> = {
  scheduled: 'bg-gray-100 text-gray-700',
  live: 'bg-green-100 text-green-700',
  complete: 'bg-blue-100 text-blue-700',
  abandoned: 'bg-red-100 text-red-700',
};

/**
 * Tennis-only match status + Start Match / Resume Scoring action. Starting a
 * match on the live scoring pad (sc-338) is Rookie-limited to 1/month
 * (ROOKIE_LIMITS.TENNIS_MATCH_SCORING) — Pro is uncapped. Resuming an
 * already-started match is never gated or counted again; only Start Match
 * consumes the month's allowance. A completed match shows the final set
 * score instead.
 */
export const TennisMatchStatusCard: React.FC<TennisMatchStatusCardProps> = ({ game, onStarted }) => {
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);
  const matchState = game.match_state ?? 'scheduled';
  const { checkFeatureAccess, showUpgradeModal, handleUpgrade, handleCloseModal } = useFeatureAccess();

  const handleStartMatch = async () => {
    if (!checkFeatureAccess('Live scoring')) return;
    setStarting(true);
    try {
      await gameService.startMatch(game.id);
      try {
        await subscriptionService.incrementUsageCounter('tennis_match_scoring_count');
      } catch (error) {
        console.error('[TennisMatchStatusCard] Error incrementing tennis match scoring count:', error);
        // Don't block the match over a failed count — it already started.
      }
      toast.success('Match started');
      onStarted();
      navigate(`/games/${game.id}/score`);
    } catch (error) {
      console.error('[TennisMatchStatusCard] Error starting match:', error);
      toast.error('Failed to start match');
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 flex items-center justify-between flex-wrap gap-3">
      <div className="flex items-center gap-3">
        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${STATE_BADGE_CLASS[matchState] ?? STATE_BADGE_CLASS.scheduled}`}>
          {STATE_LABEL[matchState] ?? 'Scheduled'}
        </span>
        {game.match_type && <span className="text-sm text-gray-600">{game.match_type}</span>}
        {game.ladder_slot && <span className="text-sm text-gray-500">{game.ladder_slot}</span>}
        {game.court && <span className="text-sm text-gray-500">Court {game.court}</span>}
        {game.started_at && (
          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
            <Clock className="w-3.5 h-3.5" />
            Started {new Date(game.started_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
          </span>
        )}
        {(matchState === 'complete' || matchState === 'abandoned') && game.our_score != null && game.opponent_score != null && (
          <span className="text-sm font-semibold text-gray-900">
            Sets: {game.our_score}–{game.opponent_score}
          </span>
        )}
      </div>
      {matchState === 'scheduled' && (
        <button
          type="button"
          onClick={handleStartMatch}
          disabled={starting}
          className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Play className="w-4 h-4" />
          {starting ? 'Starting...' : 'Start Match'}
        </button>
      )}
      {matchState === 'live' && (
        <button
          type="button"
          onClick={() => navigate(`/games/${game.id}/score`)}
          className="btn-primary flex items-center gap-2"
        >
          <Radio className="w-4 h-4" />
          Resume Scoring
        </button>
      )}
      <UpgradeModal isOpen={showUpgradeModal} onClose={handleCloseModal} onUpgrade={handleUpgrade} />
    </div>
  );
};

export default TennisMatchStatusCard;
